<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="d-flex align-center">
        <AssigneesSearchPopover
          :filters="{ canUseAIFeatures: false }"
          :includeCurrentAccountAtTop="false"
          @change="addUsers"
        >
          <Button plain icon="mdi-plus" :text="$t('people.add_user')" />
        </AssigneesSearchPopover>
      </div>

      <SearchInput
        :value="searchTerms"
        @change="searchTerms = $event"
        style="width: 250px;"
      />
    </div>

    <v-card outlined>
      <v-data-table
        calculate-widths
        data-private
        :footer-props="footerOptions"
        :headers="headers"
        :items="users"
        :loading="isLoadingUsers"
        :options.sync="options"
        :server-items-length="totalRecordsInData"
      >
        <template v-slot:[`item.account`]="{ item }">
          <ChipAvatar
            link
            outlined
            small
            :account="item"
            @click="goToProfile(item)"
          />
        </template>

        <template v-slot:[`item.aiMonthlyTokenLimit`]="{ item }">
          <v-edit-dialog :ref="`editDialog_${item._id}`">
            <v-chip small outlined class="cursor-pointer">
              {{ getMonthlyLimitText(item) }}
              <v-icon right small class="ms-1">mdi-chevron-down</v-icon>
            </v-chip>

            <template v-slot:input>
              <v-radio-group
                class="ma-0 py-0"
                column
                hide-details
                :value="item.aiMonthlyTokenLimit"
              >
                <div
                  v-for="monthlyLimit in monthlyLimitOptions"
                  class="menu-item py-2"
                  :key="monthlyLimit.id"
                >
                  <v-radio
                    class="mx-0 px-0"
                    :value="monthlyLimit.value"
                    :ripple="false"
                    @click.native.prevent.stop.capture="
                      handleMonthlyLimitChange(item, monthlyLimit.value),
                        $refs[`editDialog_${item._id}`].cancel()
                    "
                  >
                    <template v-slot:label>
                      <span class="mx-0 px-0 font-size-13 font-weight-500">
                        {{ monthlyLimit.title }}
                      </span>
                    </template>
                  </v-radio>
                </div>
              </v-radio-group>
            </template>
          </v-edit-dialog>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <Button
            color="blueGrey"
            icon="mdi-trash-can-outline"
            @click.stop="handleUserSettingsDelete(item)"
          />
        </template>
      </v-data-table>

      <UserUnlimitedTokensConfirmation
        :shown="isUnlimitedTokensConfirmationShown"
        :account="accountToConfirmUnlimitedTokens"
        @confirm="confirmUnlimitedTokensForUser"
        @cancel="cancelUnlimitedTokensConfirmation"
      />

      <DeleteConfirmationModal
        :show="isSettingsDeleteConfirmationShown"
        :title="$t('common.attention')"
        :text="
          $t(
            'ai_features.user_access_control.user_setting_delete_confirmation',
            { entityName: getFullName(accountSettingsToDelete) }
          )
        "
        @cancel="cancelUserSettingsDelete"
        @delete="confirmUserSettingsDelete"
      />
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import api from '@/api'

import SearchInput from '@/components/Core/SearchInput.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import Button from '@/components/UI/Button.vue'
import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import UserUnlimitedTokensConfirmation from './UserUnlimitedTokensConfirmation.vue'

const GLOBAL_TOKENS_LIMIT = null
const UNLIMITED_TOKENS = -1
const SORT_BY_FIELD = 'updatedAt'
const DEFAULT_SIZE = 50
const SORT_BY_COINCIDENCE = '_score'

export default {
  name: 'AccessTable',
  mixins: [mixin],

  components: {
    AssigneesSearchPopover,
    Button,
    ChipAvatar,
    DeleteConfirmationModal,
    SearchInput,
    UserUnlimitedTokensConfirmation,
  },

  data() {
    return {
      isLoadingUsers: false,
      totalRecordsInData: 0,
      searchTerms: '',
      users: [],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'account',
          show: true,
        },
        {
          text: this.$t(
            'ai_features.user_access_control.header_tokens_monthly_limit'
          ),
          sortable: false,
          value: 'aiMonthlyTokenLimit',
          show: true,
          align: 'start',
        },
        {
          value: 'actions',
          text: this.$t('common.action_other'),
          sortable: false,
          show: true,
          align: 'start',
          width: 100,
        },
      ],
      monthlyLimitOptions: [
        {
          id: 'globalLimit',
          title: this.$t(
            'ai_features.user_access_control.global_tokens_monthly_limit_option'
          ),
          value: GLOBAL_TOKENS_LIMIT,
        },
        {
          id: 'unlimited',
          title: this.$t(
            'ai_features.user_access_control.unlimited_tokens_monthly_limit_option'
          ),
          value: UNLIMITED_TOKENS,
        },
      ],
      accountToConfirmUnlimitedTokens: null,
      isUnlimitedTokensConfirmationShown: false,
      accountSettingsToDelete: null,
      isSettingsDeleteConfirmationShown: false,
    }
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: DEFAULT_SIZE,
          multiSort: false,
          mustSort: true,
          page: 1,
        }
      },
      set(event) {
        this.fetchUsersWithAccessToAIFeatures({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  mounted() {
    this.fetchUsersWithAccessToAIFeatures({ page: 1 })
  },

  watch: {
    searchTerms() {
      this.fetchUsersWithAccessToAIFeatures({ page: 1 })
    },
  },

  methods: {
    async fetchUsersWithAccessToAIFeatures(options) {
      this.isLoadingUsers = true

      const itemsPerPage = options.itemsPerPage || DEFAULT_SIZE
      const terms = this.searchTerms?.toLowerCase() ?? ''
      const sort = options?.sortBy
        ? { by: options.sortBy, order: options.sortDesc ? 'desc' : 'asc' }
        : { by: terms ? SORT_BY_COINCIDENCE : SORT_BY_FIELD, order: 'desc' }

      try {
        const response = await api.account.getAccounts(this.organizationId, {
          filters: { canUseAIFeatures: true },
          from: (options.page - 1) * itemsPerPage,
          limit: itemsPerPage,
          sort,
          terms: this.searchTerms?.toLowerCase() ?? '',
        })

        this.users = response?.data?.results ?? []
        this.totalRecordsInData = response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoadingUsers = false
      }
    },

    getMonthlyLimitText(account) {
      return account?.aiMonthlyTokenLimit === UNLIMITED_TOKENS
        ? this.$t(
            'ai_features.user_access_control.unlimited_tokens_monthly_limit_option'
          )
        : this.$t(
            'ai_features.user_access_control.global_tokens_monthly_limit_option'
          )
    },

    async addUsers({ ids }) {
      try {
        await api.ai.grantAccessToAIFeatures(this.organizationId, ids)

        this.showSuccess(
          this.$t('ai_features.user_access_control.user_access_granted_success')
        )

        setTimeout(() => {
          this.fetchUsersWithAccessToAIFeatures({ page: 1 })
        }, 2000)
      } catch (error) {
        this.showError(
          this.$t('ai_features.user_access_control.user_access_grant_error')
        )
      }
    },

    handleMonthlyLimitChange(account, monthlyLimit) {
      if (monthlyLimit === UNLIMITED_TOKENS) {
        this.accountToConfirmUnlimitedTokens = account
        this.isUnlimitedTokensConfirmationShown = true
      } else {
        this.updateAccountMonthlyTokensLimit(account, monthlyLimit)
      }
    },

    confirmUnlimitedTokensForUser() {
      this.isUnlimitedTokensConfirmationShown = false

      this.updateAccountMonthlyTokensLimit(
        this.accountToConfirmUnlimitedTokens,
        UNLIMITED_TOKENS
      )

      this.accountToConfirmUnlimitedTokens = null
    },

    cancelUnlimitedTokensConfirmation() {
      this.isUnlimitedTokensConfirmationShown = false
      this.accountToConfirmUnlimitedTokens = null
    },

    handleUserSettingsDelete(account) {
      this.accountSettingsToDelete = account
      this.isSettingsDeleteConfirmationShown = true
    },

    confirmUserSettingsDelete() {
      this.isSettingsDeleteConfirmationShown = false
      this.deleteUserSettings(this.accountSettingsToDelete)
      this.accountSettingsToDelete = null
    },

    cancelUserSettingsDelete() {
      this.isSettingsDeleteConfirmationShown = false
      this.accountSettingsToDelete = null
    },

    async updateAccountMonthlyTokensLimit(account, monthlyLimit) {
      try {
        await api.ai.updateAccountMonthlyTokensLimit(
          this.organizationId,
          account._id,
          monthlyLimit
        )

        this.showSuccess(
          this.$t(
            'ai_features.user_access_control.monthly_limit_update_success'
          )
        )

        setTimeout(() => {
          this.fetchUsersWithAccessToAIFeatures({ page: 1 })
        }, 2000)
      } catch (error) {
        this.showError(
          this.$t('ai_features.user_access_control.monthly_limit_update_error')
        )
      }
    },

    async deleteUserSettings(account) {
      try {
        await api.ai.deleteAccountAISettings(this.organizationId, account._id)

        this.showSuccess(
          this.$t('ai_features.user_access_control.user_setting_delete_success')
        )

        setTimeout(() => {
          this.fetchUsersWithAccessToAIFeatures({ page: 1 })
        }, 2000)
      } catch (error) {
        this.showError(
          this.$t('ai_features.user_access_control.user_setting_delete_error')
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.menu-item {
  border-radius: 4px;
  &:first-child {
    margin-top: 0 !important;
  }
  &:hover {
    background-color: $color-blue-grey-lighten3;
  }
}
</style>
